import {
    Component, ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    HostListener,
    Input,
    OnChanges, OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {animate, style, transition, trigger} from '@angular/animations';
import {BaseOutsideClickScrollEventListenerDirective} from '../../../directives/base-outside-click-scroll-event-listener.directive';
import {SelectOptionModel} from '../../../models/select-option-model';

@Component({
    selector: 'app-input-select',
    templateUrl: './input-select.component.html',
    styleUrls: ['./input-select.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputSelectComponent),
            multi: true,
        },
    ],
    animations: [
        trigger('fadeSlide', [
            transition(':enter', [
                style({opacity: 0, transform: 'translateY(-10px)'}),
                animate('200ms ease-out', style({opacity: 1, transform: 'translateY(0)'})),
            ]),
            transition(':leave', [
                animate('100ms ease-in', style({opacity: 0, transform: 'translateY(-10px)'})),
            ]),
        ]),
    ],
})
export class InputSelectComponent extends BaseOutsideClickScrollEventListenerDirective implements ControlValueAccessor, OnChanges {
    @Input() public label: string;
    @Input() public name: string;
    @Input() public options: SelectOptionModel<any>[] = [];
    @Input() public required: boolean | string;

    @HostBinding('class.active') isActive: boolean;

    @Input() public placeholder: string = 'Select an option';

    @Input('disabled') isDisabledInput: boolean | number | '';
    @HostBinding('class.disabled') isDisabled: boolean;

    @Input() public panelClass: string;

    @Output() public blur: EventEmitter<any> = new EventEmitter<any>();
    @Output() public focus: EventEmitter<any> = new EventEmitter<any>();

    @Input() compareByFn: (item1: any, item2: any) => boolean = this.compareByDefaultFn;

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false; // Close the dropdown
        }
    }


    public value: any;
    public isOpen: boolean = false;

    // which item is highlighted by the keyboard:
    public highlightedIndex: number = -1; // Tracks the currently highlighted option,


    public propagateChange: (_: any) => void = (_: any) => {
    };
    public onTouched: () => void = () => {
    };


    ngOnChanges(changes: SimpleChanges): void {
        if (this.isDisabledInput !== '') {
            this.isDisabled = !!this.isDisabledInput;
        } else {
            this.isDisabled = true;
        }
    }

    toggleDropdown( optionsContainer?: HTMLElement): void {
        if (!this.isDisabled) {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                if (this.value !== undefined) {
                    // Find the index of the currently selected value that is not disabled
                    this.highlightedIndex = this.options.findIndex(
                        option => this.compareByFn(option.value, this.value) && !option.isDisabled
                    );
                }

                // If no valid index is found, highlight the first non-disabled option
                if (this.highlightedIndex === -1) {
                    this.highlightedIndex = this.options.findIndex(option => !option.isDisabled);
                }
            }

            if (this.isOpen) {
                this.registerListeners(optionsContainer);
            } else {
                this.removeListeners();
            }
        }
    }

    get isRequired(): boolean {
        return this.required === '' || this.required === true;
    }

    selectOption(option: SelectOptionModel<any>): void {
        if (!this.isDisabled && !option.isDisabled) {
            if (this.value !== option.value) {
                this.value = option.value;
                this.propagateChange(this.value);
            }
            // this.isOpen = false; // Close the dropdown after selection
        }
    }

    getSelectedLabel(): string | undefined {
        return this.getValueLabel(this.value);
    }

    getLabel() {
        return this.label + (this.isRequired ? '*' : '');
    }

    writeValue(value: any): void {
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    compareByDefaultFn(item1: any, item2: any): boolean {
        return item1 === item2;
    }

    getValueLabel(value: any) {
        return this.options?.find(option => this.compareByFn(option.value, value))?.label;
    }

    onFocus() {
        this.isActive = true;
        this.focus.emit();
    }

    onBlur() {
        this.onTouched();
        this.isActive = false;
        this.blur.emit();
    }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: KeyboardEvent): void {
        if (this.isDisabled) {
            return;
        }

        switch (event.key) {
            case 'Enter':
                if (this.isOpen) {
                    if (
                        this.highlightedIndex >= 0 &&
                        this.highlightedIndex < this.options.length &&
                        !this.options[this.highlightedIndex].isDisabled
                    ) {
                        this.selectOption(this.options[this.highlightedIndex]);
                        this.isOpen = false; // Close the dropdown after selection
                    }
                } else {
                    this.toggleDropdown();
                    if (this.isOpen) {
                        this.scrollHighlightedOptionIntoView();
                    }

                }
                event.preventDefault();
                break;

            case 'ArrowDown':
                if (this.isOpen) {
                    let nextIndex = this.highlightedIndex;
                    let attempts = 0; // Prevent infinite loops if all options are disabled
                    do {
                        nextIndex = (nextIndex + 1) % this.options.length;
                        attempts++;
                    } while (this.options[nextIndex].isDisabled && attempts < this.options.length);
                    this.highlightedIndex = nextIndex;

                    this.scrollHighlightedOptionIntoView();
                } else {
                    this.toggleDropdown();
                }
                event.preventDefault();
                break;

            case 'ArrowUp':
                if (this.isOpen) {
                    let prevIndex = this.highlightedIndex;
                    let attempts = 0; // Prevent infinite loops if all options are disabled
                    do {
                        prevIndex = (prevIndex - 1 + this.options.length) % this.options.length;
                        attempts++;
                    } while (this.options[prevIndex].isDisabled && attempts < this.options.length);
                    this.highlightedIndex = prevIndex;
                    this.scrollHighlightedOptionIntoView();
                }
                event.preventDefault();
                break;

            case 'Escape':
                if (this.isOpen) {
                    this.isOpen = false;
                }
                event.preventDefault();
                break;
        }
    }

    isHighlighted(index: number): boolean {
        return this.highlightedIndex === index;
    }

    scrollHighlightedOptionIntoView(scrollFast = false): void {
        setTimeout(() => {
            const optionsContainer = document.querySelector('.options-container');
            const highlightedOption = optionsContainer?.querySelector('.option.highlighted');
            if (scrollFast) {
                highlightedOption?.scrollIntoView({behavior: 'auto', block: 'nearest'});
            } else {
                highlightedOption?.scrollIntoView({behavior: 'smooth', block: 'nearest'});

            }

        });

    }

    override onScroll() {
        this.isOpen = false;
        this.removeListeners();
    }

    override onOutsideClick() {
        this.isOpen = false;
        this.removeListeners();
    }
}
